import { useEffect, useState } from "react";
import { Button, Form, Input, Modal, Popconfirm, Table, message } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { OrgInfo } from "@/stores/management";
import { orgService } from "@/services/organization";
import { useCardExtra } from "@/layouts/ManagementLayout";
import { formatDateTime } from "@/utils/date";

export function OrgManagement() {
  const { t } = useTranslation("management");
  const [orgs, setOrgs] = useState<OrgInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<OrgInfo | null>(null);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [editForm] = Form.useForm();

  const loadOrgs = async () => {
    setLoading(true);
    try { setOrgs(await orgService.list()); }
    catch { setOrgs([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadOrgs(); }, []);

  useCardExtra(
    <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
      {t("orgs.create")}
    </Button>,
    [t],
  );

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await orgService.create(newName.trim());
      setNewName("");
      setCreateOpen(false);
      message.success(t("common:success"));
      loadOrgs();
    } catch { message.error(t("common:error")); }
    finally { setSaving(false); }
  };

  const openEdit = (org: OrgInfo) => {
    setEditingOrg(org);
    editForm.setFieldsValue({ name: org.name, plan: org.plan || "" });
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!editingOrg) return;
    const values = await editForm.validateFields();
    setSaving(true);
    try {
      await orgService.update(editingOrg.id, { name: values.name, plan: values.plan || undefined });
      setEditOpen(false);
      message.success(t("common:success"));
      loadOrgs();
    } catch { message.error(t("common:error")); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try {
      await orgService.delete(id);
      message.success(t("common:success"));
      loadOrgs();
    } catch { message.error(t("common:error")); }
  };

  const columns = [
    { title: t("orgs.table.name"), dataIndex: "name", key: "name" },
    { title: t("orgs.table.plan"), dataIndex: "plan", key: "plan", render: (v: string | null) => v || "—" },
    {
      title: t("orgs.table.created_at"), dataIndex: "created_at", key: "created_at",
      render: (v: string) => formatDateTime(v),
    },
    {
      title: t("common:action"), key: "action", width: 100,
      render: (_: unknown, record: OrgInfo) => (
        <span style={{ display: "flex", gap: 4 }}>
          <Button type="text" icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
          <Popconfirm title={t("orgs.delete.confirm")} onConfirm={() => handleDelete(record.id)}>
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </span>
      ),
    },
  ];

  return (
    <>
      <Table dataSource={orgs} columns={columns} rowKey="id" loading={loading} size="middle" />

      <Modal title={t("orgs.create")} open={createOpen} onOk={handleCreate} onCancel={() => setCreateOpen(false)} confirmLoading={saving}>
        <Input
          placeholder={t("context.create_org.placeholder")}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onPressEnter={handleCreate}
        />
      </Modal>

      <Modal title={t("common:edit")} open={editOpen} onOk={handleEdit} onCancel={() => setEditOpen(false)} confirmLoading={saving}>
        <Form form={editForm} layout="vertical">
          <Form.Item label={t("orgs.table.name")} name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t("orgs.table.plan")} name="plan">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
